// Dashboard — Strata operations overview, wired to GET /v1/stats and
// GET /v1/merchants (Phase 06.1).
//
// Everything on this screen comes from the real backend. The illustrative
// throughput chart, rail-mix card, sparklines, and dead time-range control
// were stripped during the dead-UI cleanup pass — they suggested live
// telemetry that did not exist. Re-add them once a time-series / rail-mix
// endpoint actually ships.
import { useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";

import { I } from "@/components/Icons";
import { Kpi } from "@/components/UI";
import { DateRangeFilter } from "@/components/DateRangeFilter";
import {
  fmtCcyMinorExact,
  fmtMoney,
  formatDateTime,
  formatDay,
} from "@/utils/format";
import { downloadCsv } from "@/utils/csv";
import { dateRangeBounds, periodPresets, ymdLocal } from "@/utils/dateRange";
import { useStats } from "@/queries/useStats";
import { useMerchants } from "@/queries/useMerchants";
import { usePayoutBreakdown } from "@/queries/usePayoutBreakdown";
import { getPaysmartBalance, type PaysmartBalance } from "@/api/paysmartBalance";
import {
  useCurrentMerchantStatus,
  isMerchantBlocked,
} from "@/queries/useCurrentMerchantStatus";
import { useAuth, effectiveRole, isAdminRole } from "@/store/auth";
import { useUI } from "@/store/ui";

export function Dashboard() {
  const navigate = useNavigate();
  const user = useAuth((s) => s.user);
  const role = useAuth((s) => effectiveRole(s));

  // Period the figures cover. Lives in the URL (?from=&to=, local
  // "YYYY-MM-DD") rather than component state so drilling into a tile and
  // coming back keeps the period, and a link to the dashboard can carry
  // one. No params = today: a lifetime count answers no operational
  // question, and "today" is what an operator opening the console wants.
  //
  // Bounds are resolved in the operator's timezone — the same instants the
  // payouts list sends for the same dates — so a tile and the list it opens
  // count the same rows.
  const [searchParams, setSearchParams] = useSearchParams();
  const today = useMemo(() => ymdLocal(new Date()), []);
  const dateFrom = searchParams.get("from") ?? today;
  const dateTo = searchParams.get("to") ?? today;
  const setRange = useCallback(
    ({ from, to }: { from: string; to: string }) => {
      const next = new URLSearchParams(searchParams);
      next.set("from", from);
      next.set("to", to);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );
  const presets = useMemo(() => periodPresets(), []);
  const range = useMemo(
    () => dateRangeBounds(dateFrom, dateTo),
    [dateFrom, dateTo],
  );
  // What to call the period in labels: the preset's own name when one is
  // selected, the dates otherwise. Lower-case so it reads inside a sentence.
  const periodLabel = useMemo(() => {
    const p = presets.find((x) => x.from === dateFrom && x.to === dateTo);
    if (p) return p.label.toLowerCase();
    if (dateFrom === "" && dateTo === "") return "all time";
    if (dateFrom === "") return `up to ${formatDay(dateTo)}`;
    if (dateTo === "") return `from ${formatDay(dateFrom)}`;
    if (dateFrom === dateTo) return formatDay(dateFrom);
    return `${formatDay(dateFrom)} – ${formatDay(dateTo)}`;
  }, [presets, dateFrom, dateTo]);
  // Tile deep-links carry the same window so the list shows the counted rows.
  const listLink = (status: string) =>
    `/payouts?status=${status}&from=${dateFrom}&to=${dateTo}`;

  // stats is still read for the stale-pending sweep (admin-only); every
  // tile comes from the breakdown.
  const { data: stats } = useStats(range);
  const { data: merchants = [] } = useMerchants();
  const currentMerchantId = useUI((s) => s.currentMerchantId);
  const { data: breakdown, isLoading } = usePayoutBreakdown(range);

  // PDF round 2: when the current merchant is suspended/closed, block
  // the "New payout" button client-side. Server-side enforcement still
  // runs (assertMerchantActive in payout.Service), this is the UX
  // pre-emption so operators see the gate before submitting.
  const { status: merchantStatus } = useCurrentMerchantStatus();
  const blocked = isMerchantBlocked(merchantStatus);

  // ONE set of tiles. Each is a status: how many payouts created in the
  // period are in it, and how much money. Every tile covers the same
  // period and opens exactly the rows it counted — there used to be three
  // overlapping panels of these numbers over two different windows, plus
  // an "Approval queue" card that listed failures.
  const count = (st: string) => breakdown?.counts[st] ?? 0;
  const inr = (st: string) => breakdown?.amounts[st]?.["INR"] ?? 0n;
  // Non-INR money (USD rails) is named alongside rather than dropped.
  const otherCcy = (st: string) =>
    Object.entries(breakdown?.amounts[st] ?? {})
      .filter(([ccy, v]) => ccy !== "INR" && v !== 0n)
      .map(([ccy, v]) => ` + ${fmtMoney(v, ccy)}`)
      .join("");
  const n = (st: string) =>
    `${count(st).toLocaleString("en-IN")} payout${count(st) === 1 ? "" : "s"}`;
  const total = breakdown?.total ?? 0;
  const named = ["needs_approval", "pending", "success", "failure"].reduce(
    (a, st) => a + count(st),
    0,
  );
  // Statuses outside the four tiles (pending_bene_registration, or one
  // added later) are counted in the total and named below, never hidden.
  const unaccounted = Math.max(0, total - named);

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="eyebrow">
            Console · {isAdminRole(role) ? "Platform Admin" : "Merchant"}
            {user && (
              <>
                {" · "}
                <span style={{ color: "var(--ink-2)" }}>{user.email}</span>
              </>
            )}
          </div>
          <h1>Operations overview</h1>
          <div className="sub">
            {isLoading
              ? "Loading…"
              : `${total.toLocaleString("en-IN")} payout${total === 1 ? "" : "s"} created ${periodLabel}` +
                (currentMerchantId == null ? " · all merchants" : "")}
          </div>
        </div>
        <div className="actions">
          {/* Export is admin-only: it dumps the full merchants list,
              which a merchant user has no business seeing. PDF review
              26 May 26: "Remove the Export from the merchant access —
              Overview." */}
          {isAdminRole(role) && (
            <button
              className="btn"
              type="button"
              onClick={() =>
                downloadCsv(
                  "merchants.csv",
                  merchants.map((m) => ({
                    id: m.id,
                    slug: m.slug,
                    display_name: m.display_name,
                    status: m.status,
                    created_at: m.created_at,
                  })),
                )
              }
            >
              <I.Download size={12} /> Export
            </button>
          )}
          <button
            className="btn accent"
            type="button"
            onClick={() => navigate("/payouts?new=1")}
            disabled={blocked}
            title={
              blocked
                ? `New payouts are blocked while this merchant is ${merchantStatus}.`
                : undefined
            }
          >
            <I.Plus size={12} /> New payout
          </button>
        </div>
      </div>

      {/* Period picker. Open, not behind a toggle: the period is the
          question the dashboard answers, not a refinement of it. */}
      <div className="toolbar" style={{ marginTop: 8 }}>
        <DateRangeFilter
          from={dateFrom}
          to={dateTo}
          onChange={setRange}
          presets={presets}
          label="Period"
        />
      </div>

      {/* Merchant bank-rail balance — prominent card. Surfaced ONLY when
          the dashboard is scoped to a single merchant (server-side endpoint
          requires the X-Merchant-ID header anyway). Polls at 30s; refresh
          button forces an immediate refetch. It is a balance NOW, not a
          figure for the period, which is why it sits apart from the tiles. */}
      {currentMerchantId != null && (
        <PaysmartBalanceCard merchantId={currentMerchantId} />
      )}

      <div className="kpi-grid">
        <Kpi
          k="Settled"
          v={fmtCcyMinorExact(inr("success"))}
          ccy="INR"
          delta={`${n("success")}${otherCcy("success")}`}
          up={count("success") > 0}
          onClick={() => navigate(listLink("success"))}
          title={`Open the payouts settled ${periodLabel}`}
        />
        <Kpi
          k="Awaiting approval"
          v={fmtCcyMinorExact(inr("needs_approval"))}
          ccy="INR"
          delta={`${n("needs_approval")}${otherCcy("needs_approval")} · no money has moved`}
          onClick={() => navigate(listLink("needs_approval"))}
          title={`Open the payouts created ${periodLabel} that are awaiting approval`}
        />
        <Kpi
          k="In flight"
          v={fmtCcyMinorExact(inr("pending"))}
          ccy="INR"
          delta={`${n("pending")}${otherCcy("pending")} · dispatched, not yet resolved`}
          onClick={() => navigate(listLink("pending"))}
          title={`Open the payouts created ${periodLabel} that are still in flight`}
        />
        <Kpi
          k="Failed"
          v={fmtCcyMinorExact(inr("failure"))}
          ccy="INR"
          delta={`${n("failure")}${otherCcy("failure")}`}
          warn={count("failure") > 0}
          onClick={() => navigate(listLink("failure"))}
          title={`Open the payouts that failed ${periodLabel}`}
        />
      </div>
      <p className="dim" style={{ fontSize: 11, marginTop: 8 }}>
        Every figure covers payouts created {periodLabel}, and each tile opens
        exactly the payouts it counts.
        {unaccounted > 0 &&
          ` ${unaccounted.toLocaleString("en-IN")} more are in another status (e.g. awaiting beneficiary registration) and are in the total above.`}
      </p>

      {/* Stale-pending alert. Admin-only — surfaces payouts that have
          been pending for >24h, meaning the reconciler has been polling
          them indefinitely without a terminal result from the bank rail.
          Hidden when count==0 so the alert is signal-not-noise. Never
          windowed: a payout stuck since last month is exactly the one it
          exists to surface. */}
      {isAdminRole(role) && stats?.stale_pending.count != null &&
        stats.stale_pending.count > 0 && (
          <StalePendingAlert
            count={stats.stale_pending.count}
            totalMinor={stats.stale_pending.total_minor}
            oldestAgeHours={stats.stale_pending.oldest_age_hours}
            onClick={() => navigate("/payouts?status=pending&range=all")}
          />
        )}
    </div>
  );
}

// ─── StalePendingAlert ───────────────────────────────────────────────────────
//
// Surfaces payouts stuck pending >24h. The reconciler keeps polling them
// indefinitely (RECONCILE_GIVEUP_AFTER=0 in prod), so without this card an
// operator has no signal that something is wrong upstream. Clicking the
// card navigates to /payouts where the row-age highlight makes the stale
// rows visible in the table.
//
// Visual register: amber/warn — not red. A stale-pending row is not
// confirmed failed (that would be terminal); it's "we don't know what
// happened, escalate". Red is for terminal failures.

function StalePendingAlert({
  count,
  totalMinor,
  oldestAgeHours,
  onClick,
}: {
  count: number;
  totalMinor: bigint;
  oldestAgeHours: number;
  onClick: () => void;
}) {
  // Human-readable age. Under 48h shows hours; beyond that, days.
  const ageLabel =
    oldestAgeHours >= 48
      ? `${Math.floor(oldestAgeHours / 24)} days`
      : `${oldestAgeHours} hours`;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      className="card"
      style={{
        marginTop: "var(--card-gap)",
        cursor: "pointer",
        borderLeft: "3px solid var(--warn, #b42318)",
        background: "var(--warn-soft, #fef3f2)",
      }}
      title="Open the Payouts page to see which rows are stuck"
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          padding: 16,
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 16,
            background: "var(--warn, #b42318)",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <I.AlertCircle size={16} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>
            {count} payout{count === 1 ? "" : "s"} stuck pending — escalate
            to bank rail support
          </div>
          <div
            className="dim"
            style={{ fontSize: 12, marginTop: 4, lineHeight: 1.5 }}
          >
            {fmtMoney(totalMinor, "INR")} in committed funds across{" "}
            {count} row{count === 1 ? "" : "s"}. Oldest has been pending for{" "}
            {ageLabel}. The reconciler keeps polling, but upstream hasn't
            returned a terminal status — open a ticket with the bank rail
            provider and include the row's transaction_id.
          </div>
        </div>
        <I.ChevronRight size={14} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Merchant bank-rail balance card
// ---------------------------------------------------------------------------
//
// Prominent dashboard surface for the merchant's available balance at
// the upstream bank rail. Calls GET /v1/merchants/:id/paysmart-balance
// which hits the IDFC GetMerchantBalance endpoint live every request
// (no DB cache).
//
// Polls at 30s — same cadence as the KPI grid. Operator can force a
// refetch via the Refresh button. Failure modes render inline with a
// hint, not a toast — the card is the canonical source of truth for
// "how much can I pay out right now" and a transient error doesn't
// invalidate the entire dashboard.

function PaysmartBalanceCard({ merchantId }: { merchantId: number }) {
  const { data, isLoading, isFetching, refetch } = useQuery<PaysmartBalance>({
    queryKey: ["paysmart-balance", merchantId],
    queryFn: () => getPaysmartBalance(merchantId),
    refetchInterval: 30_000,
    // Keep showing the last-good value while a refetch is in flight so
    // the operator doesn't see the number flicker to 0.
    staleTime: 10_000,
  });

  const live = data?.source === "live" && data.amount_minor != null;
  const errored = data?.source === "error";
  const notConnected = data?.source === "not_connected";
  // Operator opted this merchant out of any live balance display.
  const hidden = data?.source === "hidden";
  // Ledger source — operator-managed. The backend always populates
  // amount_minor on this branch (even ₹0 with error="no_ledger_entries"
  // when nothing has been entered yet); we discriminate those two cases
  // off `error` so the operator sees "Not initialized" before a baseline
  // exists instead of a misleading ₹0.00.
  const ledger = data?.source === "ledger";
  const ledgerInitialized = ledger && data?.error !== "no_ledger_entries";
  const ledgerUninitialized = ledger && data?.error === "no_ledger_entries";
  // Eyebrow label flips with the source so the operator always knows
  // which surface is feeding the number under the prominent card.
  const eyebrow = hidden
    ? "Balance · Hidden"
    : ledger
      ? "Ledger balance · Operator-managed"
      : "Bank balance · Live";

  return (
    <div
      style={{
        background:
          "linear-gradient(135deg, var(--accent, #0e8a3e) 0%, var(--accent-2, #0c6b30) 100%)",
        borderRadius: 12,
        padding: "20px 24px",
        marginBottom: 20,
        color: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 24,
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
      }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontSize: 10,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            opacity: 0.78,
            marginBottom: 10,
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <span>{eyebrow}</span>
          {data?.rail_label && !hidden && !ledger && (
            <>
              <span style={{ opacity: 0.5 }}>·</span>
              <span
                className="mono"
                style={{
                  opacity: 0.85,
                  fontSize: 10,
                  letterSpacing: "0.04em",
                  textTransform: "none",
                }}
              >
                {data.rail_label}
              </span>
            </>
          )}
        </div>
        <div
          style={{
            // Match the KPI cards' numeric style (Geist Mono + tabular
            // nums) but scaled up for prominence. The previous serif at
            // 36px read as wispy on the gradient background.
            fontFamily: "var(--font-mono)",
            fontVariantNumeric: "tabular-nums",
            fontSize: 42,
            fontWeight: 600,
            lineHeight: 1.05,
            letterSpacing: "-0.02em",
          }}
        >
          {isLoading
            ? "—"
            : live
              ? fmtMoney(data.amount_minor!, data.currency ?? "INR")
              : ledgerInitialized && data!.amount_minor != null
                ? fmtMoney(data!.amount_minor, data!.currency ?? "INR")
                : ledgerUninitialized
                  ? "Not initialized"
                  : hidden
                    ? "—"
                    : notConnected
                      ? "Not connected"
                      : "Unavailable"}
        </div>
        <div
          style={{
            fontSize: 11,
            marginTop: 8,
            opacity: 0.78,
            letterSpacing: "0.01em",
          }}
        >
          {live && data.as_of && (
            <>As of {formatDateTime(data.as_of)}</>
          )}
          {ledgerInitialized && data?.as_of && (
            <>As of {formatDateTime(data.as_of)} · entries minus successful payouts</>
          )}
          {ledgerUninitialized &&
            "No ledger entries yet — add an opening balance on the merchant page."}
          {hidden &&
            "Balance display is hidden by the platform for this merchant."}
          {notConnected &&
            "No active bank rail for this merchant — add one on the Bank rails page."}
          {/* The backend now sends one operator-facing sentence naming the
              institution (see rail_error_message.go). Prefixing it with our
              own "Couldn't reach the bank" produced two half-sentences that
              disagreed — "Couldn't reach the bank. IDFC is not issuing us an
              access token" is not a thing anyone says. Render theirs. */}
          {errored && (data?.error ?? "We couldn't get an answer from the bank.")}
        </div>
      </div>
      <button
        type="button"
        onClick={() => refetch()}
        disabled={isFetching}
        title="Force-refresh — bypasses the 30s polling cycle"
        style={{
          background: "rgba(255,255,255,0.18)",
          border: "1px solid rgba(255,255,255,0.3)",
          color: "white",
          borderRadius: 6,
          padding: "8px 14px",
          fontSize: 12,
          fontWeight: 500,
          cursor: isFetching ? "default" : "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          opacity: isFetching ? 0.6 : 1,
          flexShrink: 0,
        }}
      >
        <I.Refresh
          size={12}
          style={{
            animation: isFetching ? "spin 800ms linear infinite" : undefined,
          }}
        />
        {isFetching ? "Refreshing" : "Refresh"}
      </button>
    </div>
  );
}
